import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, X, Truck, Package, ArrowRight, Loader2, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { publicFetch } from '@/lib/api';
import usePageMeta from '@/hooks/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== CART CONTEXT =====
const CartContext = createContext();

const CART_KEY = 'elevatemn_cart';

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
};

const saveCart = (items) => localStorage.setItem(CART_KEY, JSON.stringify(items));

const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCart);

  useEffect(() => { saveCart(items); }, [items]);

  const addItem = (product, variantLabel = '', qty = 1) => {
    setItems(prev => {
      const key = `${product.id}_${variantLabel}`;
      const existing = prev.find(i => `${i.product_id}_${i.variant_label}` === key);
      if (existing) {
        return prev.map(i => `${i.product_id}_${i.variant_label}` === key ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, { product_id: product.id, product_name: product.name, variant_label: variantLabel, quantity: qty, unit_price: product.price, image: product.images?.[0] || '' }];
    });
  };

  const updateQuantity = (productId, variantLabel, qty) => {
    if (qty <= 0) return removeItem(productId, variantLabel);
    setItems(prev => prev.map(i => i.product_id === productId && i.variant_label === variantLabel ? { ...i, quantity: qty } : i));
  };

  const removeItem = (productId, variantLabel) => {
    setItems(prev => prev.filter(i => !(i.product_id === productId && i.variant_label === variantLabel)));
  };

  const clearCart = () => setItems([]);
  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQuantity, removeItem, clearCart, totalItems, subtotal }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

// ===== SHOP PAGE =====
const ShopPageContent = () => {
  usePageMeta({
    title: 'Shop - ElevateMN Merch',
    description: 'Support the mission. Rep the movement. Shop ElevateMN merchandise — apparel, accessories, and more.',
    image: '/images/site/logo.png',
    url: 'https://elevatingmn.org/shop',
  });
  const [products, setProducts] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const cart = useCart();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, settRes] = await Promise.all([
        publicFetch(`${API}/store/products`),
        publicFetch(`${API}/store/settings`),
      ]);
      if (prodRes.ok) setProducts(await prodRes.json());
      if (settRes.ok) setStoreSettings(await settRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const categories = ['all', ...new Set(products.map(p => p.category))];
  const filtered = filter === 'all' ? products : products.filter(p => p.category === filter);

  // Store closed
  if (storeSettings && !storeSettings.store_enabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="store-closed">
        <div className="text-center max-w-md">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-brand-dark mb-2">Shop Coming Soon</h2>
          <p className="text-gray-600">{storeSettings.store_closed_message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shop-page">
      {/* Hero */}
      <section className="bg-brand-dark py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="shop-title">Shop</h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">Support the mission. Rep the movement.</p>
        </div>
      </section>

      {/* Cart Button (floating) */}
      <button
        onClick={() => setCartOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-brand-dark text-white p-4 shadow-lg hover:bg-brand-dark/90 transition-all flex items-center gap-2"
        data-testid="cart-fab"
      >
        <ShoppingCart className="w-5 h-5" />
        {cart.totalItems > 0 && (
          <span className="bg-brand-gold text-brand-dark text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
            {cart.totalItems}
          </span>
        )}
      </button>

      {/* Filters */}
      {categories.length > 2 && (
        <div className="mx-auto max-w-7xl px-6 lg:px-8 pt-8">
          <div className="flex gap-2 flex-wrap" data-testid="category-filters">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === c ? 'bg-brand-dark text-white' : 'bg-white text-brand-dark border hover:bg-gray-50'}`}
                data-testid={`filter-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Product Grid */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No products available right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
            {filtered.map(p => (
              <ProductCard key={p.id} product={p} onClick={() => setSelectedProduct(p)} />
            ))}
          </div>
        )}
      </section>

      {/* Shipping info */}
      {storeSettings && (
        <section className="mx-auto max-w-7xl px-6 lg:px-8 pb-12">
          <div className="flex items-center gap-3 justify-center text-sm text-gray-500">
            <Truck className="w-4 h-4" />
            <span>
              Flat rate shipping: ${storeSettings.shipping_fee?.toFixed(2)}
              {storeSettings.free_shipping_threshold > 0 && ` | Free on orders over $${storeSettings.free_shipping_threshold.toFixed(2)}`}
            </span>
          </div>
        </section>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}

      {/* Cart Drawer */}
      {cartOpen && (
        <CartDrawer storeSettings={storeSettings} onClose={() => setCartOpen(false)} />
      )}
    </div>
  );
};


// ===== PRODUCT CARD =====
const ProductCard = ({ product, onClick }) => {
  const outOfStock = product.has_variants
    ? (product.variants || []).every(v => (v.stock || 0) <= 0)
    : (product.stock || 0) <= 0;

  return (
    <div
      className="bg-white border hover:shadow-md transition-shadow cursor-pointer group"
      onClick={onClick}
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-square bg-gray-100 overflow-hidden relative">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-gray-300" />
          </div>
        )}
        {outOfStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white text-brand-dark text-sm font-bold px-4 py-2">SOLD OUT</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <span className="text-xs text-gray-400 uppercase tracking-wider">{product.category}</span>
        <h3 className="font-semibold text-brand-dark mt-1 line-clamp-2">{product.name}</h3>
        <p className="text-lg font-bold text-brand-dark mt-1">${product.price.toFixed(2)}</p>
      </div>
    </div>
  );
};


// ===== PRODUCT MODAL =====
const ProductModal = ({ product, onClose }) => {
  const cart = useCart();
  const [selectedVariant, setSelectedVariant] = useState('');
  const [qty, setQty] = useState(1);
  const [currentImage, setCurrentImage] = useState(0);
  const [added, setAdded] = useState(false);
  const scrollRef = useRef(null);

  const outOfStock = product.has_variants
    ? selectedVariant
      ? (product.variants.find(v => v.label === selectedVariant)?.stock || 0) <= 0
      : (product.variants || []).every(v => (v.stock || 0) <= 0)
    : (product.stock || 0) <= 0;

  const maxStock = product.has_variants
    ? (product.variants.find(v => v.label === selectedVariant)?.stock || 0)
    : (product.stock || 0);

  const handleAdd = () => {
    if (product.has_variants && !selectedVariant) return;
    cart.addItem(product, selectedVariant, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const images = product.images?.length > 0 ? product.images : [null];
  const totalImages = images.length;

  const scrollToImage = (idx) => {
    setCurrentImage(idx);
    scrollRef.current?.children[idx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.offsetWidth);
    if (idx !== currentImage) setCurrentImage(idx);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="product-modal">
        <div className="flex justify-end p-3">
          <button onClick={onClose} data-testid="close-product-modal"><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 pb-8">
          {/* Image Carousel */}
          <div className="relative">
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
              data-testid="image-carousel"
            >
              {images.map((img, i) => (
                <div key={i} className="aspect-square bg-gray-100 flex-shrink-0 w-full snap-center">
                  {img ? (
                    <img src={img} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Package className="w-16 h-16 text-gray-300" /></div>
                  )}
                </div>
              ))}
            </div>
            {/* Arrow navigation */}
            {totalImages > 1 && (
              <>
                {currentImage > 0 && (
                  <button onClick={() => scrollToImage(currentImage - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 shadow" data-testid="carousel-prev">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                {currentImage < totalImages - 1 && (
                  <button onClick={() => scrollToImage(currentImage + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 shadow" data-testid="carousel-next">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
            {/* Dots indicator */}
            {totalImages > 1 && (
              <div className="flex justify-center gap-1.5 mt-2">
                {images.map((_, i) => (
                  <button key={i} onClick={() => scrollToImage(i)} className={`w-2 h-2 rounded-full transition-colors ${i === currentImage ? 'bg-brand-dark' : 'bg-gray-300'}`} />
                ))}
              </div>
            )}
            {/* Thumbnail strip (desktop) */}
            {totalImages > 1 && (
              <div className="hidden md:flex gap-2 mt-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => scrollToImage(i)} className={`w-14 h-14 border overflow-hidden ${currentImage === i ? 'border-brand-dark border-2' : 'border-gray-200'}`}>
                    {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <Package className="w-full h-full p-2 text-gray-300" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <span className="text-xs text-gray-400 uppercase tracking-wider">{product.category}</span>
            <h2 className="text-2xl font-bold text-brand-dark mt-1">{product.name}</h2>
            <p className="text-3xl font-bold text-brand-dark mt-2">${product.price.toFixed(2)}</p>
            {product.description && <p className="text-gray-600 text-sm mt-3 whitespace-pre-line">{product.description}</p>}

            {/* Variants */}
            {product.has_variants && product.variants?.length > 0 && (
              <div className="mt-4">
                <Label className="text-sm font-medium text-gray-700">Select Option</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {product.variants.map(v => (
                    <button
                      key={v.label}
                      onClick={() => setSelectedVariant(v.label)}
                      disabled={(v.stock || 0) <= 0}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        selectedVariant === v.label
                          ? 'bg-brand-dark text-white border-brand-dark'
                          : (v.stock || 0) <= 0
                            ? 'bg-gray-100 text-gray-300 cursor-not-allowed line-through'
                            : 'bg-white text-brand-dark hover:border-brand-dark'
                      }`}
                      data-testid={`variant-btn-${v.label}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-4">
              <Label className="text-sm font-medium text-gray-700">Quantity</Label>
              <div className="flex items-center gap-3 mt-2">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-9 h-9 flex items-center justify-center border hover:bg-gray-50" data-testid="qty-minus">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-lg font-bold w-8 text-center" data-testid="qty-display">{qty}</span>
                <button onClick={() => setQty(q => Math.min(maxStock || 99, q + 1))} className="w-9 h-9 flex items-center justify-center border hover:bg-gray-50" data-testid="qty-plus">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAdd}
              disabled={outOfStock || (product.has_variants && !selectedVariant)}
              className="w-full mt-6 h-12 text-base"
              data-testid="add-to-cart-btn"
            >
              {outOfStock ? 'Sold Out' : added ? 'Added!' : 'Add to Cart'}
            </Button>

            {product.has_variants && !selectedVariant && !outOfStock && (
              <p className="text-xs text-gray-400 mt-2 text-center">Please select an option above</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ===== CART DRAWER =====
const CartDrawer = ({ storeSettings, onClose }) => {
  const cart = useCart();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', line1: '', line2: '', city: '', state: '', zip: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_type, discount_value}
  const [promoError, setPromoError] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);

  const shippingFee = storeSettings?.shipping_fee || 5;
  const freeThreshold = storeSettings?.free_shipping_threshold || 0;
  const actualShipping = (freeThreshold > 0 && cart.subtotal >= freeThreshold) ? 0 : shippingFee;

  // Calculate discount
  let discountAmount = 0;
  if (promoApplied) {
    if (promoApplied.discount_type === 'percent') {
      discountAmount = cart.subtotal * (promoApplied.discount_value / 100);
    } else {
      discountAmount = Math.min(promoApplied.discount_value, cart.subtotal);
    }
  }
  const discountedSubtotal = Math.max(cart.subtotal - discountAmount, 0.50);
  const total = discountedSubtotal + actualShipping;

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoError('');
    setValidatingPromo(true);
    try {
      const res = await publicFetch(`${API}/store/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode }),
      });
      if (res.ok) {
        const data = await res.json();
        setPromoApplied(data);
        setPromoError('');
      } else {
        const err = await res.json().catch(() => ({}));
        setPromoError(err.detail || 'Invalid code');
        setPromoApplied(null);
      }
    } catch {
      setPromoError('Could not validate code');
    }
    setValidatingPromo(false);
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await publicFetch(`${API}/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.items.map(i => ({ product_id: i.product_id, variant_label: i.variant_label, quantity: i.quantity })),
          customer_name: form.name,
          customer_email: form.email,
          customer_phone: form.phone,
          shipping_address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip },
          origin_url: window.location.origin,
          discount_code: promoApplied?.code || '',
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Checkout failed');
      }

      const data = await res.json();
      window.location.href = data.url; // Redirect to Stripe
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full overflow-y-auto shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-brand-dark flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" /> Your Cart ({cart.totalItems})
          </h2>
          <button onClick={onClose} data-testid="close-cart"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {cart.items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Your cart is empty</p>
            </div>
          </div>
        ) : !showCheckout ? (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.items.map(item => (
                <div key={`${item.product_id}_${item.variant_label}`} className="flex gap-3 bg-gray-50 p-3" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-16 h-16 bg-gray-200 flex-shrink-0 overflow-hidden">
                    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-full h-full p-3 text-gray-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-brand-dark truncate">{item.product_name}</p>
                    {item.variant_label && <p className="text-xs text-gray-500">{item.variant_label}</p>}
                    <p className="text-sm font-bold text-brand-dark">${item.unit_price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={() => cart.updateQuantity(item.product_id, item.variant_label, item.quantity - 1)} className="w-6 h-6 flex items-center justify-center border text-xs hover:bg-gray-100">-</button>
                      <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                      <button onClick={() => cart.updateQuantity(item.product_id, item.variant_label, item.quantity + 1)} className="w-6 h-6 flex items-center justify-center border text-xs hover:bg-gray-100">+</button>
                      <button onClick={() => cart.removeItem(item.product_id, item.variant_label)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="border-t p-4 space-y-2">
              {/* Promo Code */}
              <div className="mb-2">
                {promoApplied ? (
                  <div className="flex items-center justify-between bg-green-50 px-3 py-2 text-sm" data-testid="promo-applied">
                    <span className="flex items-center gap-1.5 text-green-700">
                      <Tag className="w-3.5 h-3.5" />
                      <span className="font-mono font-medium">{promoApplied.code}</span>
                      <span>({promoApplied.discount_type === 'percent' ? `${promoApplied.discount_value}% off` : `$${promoApplied.discount_value} off`})</span>
                    </span>
                    <button onClick={removePromo} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-1.5">
                      <Input
                        placeholder="Promo code"
                        value={promoCode}
                        onChange={e => setPromoCode(e.target.value.toUpperCase())}
                        className="font-mono text-sm h-9"
                        data-testid="promo-code-input"
                      />
                      <Button variant="outline" size="sm" onClick={applyPromo} disabled={validatingPromo || !promoCode.trim()} className="h-9 px-3" data-testid="apply-promo-btn">
                        {validatingPromo ? '...' : 'Apply'}
                      </Button>
                    </div>
                    {promoError && <p className="text-xs text-red-500 mt-1">{promoError}</p>}
                  </div>
                )}
              </div>

              <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span>${cart.subtotal.toFixed(2)}</span></div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span><span>-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Shipping</span>
                <span>{actualShipping === 0 ? <span className="text-green-600 font-medium">FREE</span> : `$${actualShipping.toFixed(2)}`}</span>
              </div>
              {freeThreshold > 0 && cart.subtotal < freeThreshold && (
                <p className="text-xs text-gray-400">Add ${(freeThreshold - cart.subtotal).toFixed(2)} more for free shipping</p>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span>${total.toFixed(2)}</span></div>
              <Button className="w-full h-12 text-base mt-2" onClick={() => setShowCheckout(true)} data-testid="proceed-to-checkout-btn">
                Checkout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        ) : (
          /* Checkout Form */
          <form onSubmit={handleCheckout} className="flex-1 overflow-y-auto p-4 space-y-4">
            <button type="button" onClick={() => setShowCheckout(false)} className="text-sm text-gray-500 hover:text-brand-dark flex items-center gap-1 mb-2">
              &larr; Back to cart
            </button>
            <h3 className="text-lg font-bold text-brand-dark">Shipping Details</h3>

            <div>
              <Label>Full Name *</Label>
              <Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="checkout-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="checkout-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="checkout-phone" />
            </div>

            <hr />
            <h4 className="text-sm font-semibold text-gray-700">Shipping Address</h4>

            <div>
              <Label>Address Line 1 *</Label>
              <Input required value={form.line1} onChange={e => setForm(f => ({ ...f, line1: e.target.value }))} data-testid="checkout-address1" />
            </div>
            <div>
              <Label>Address Line 2</Label>
              <Input value={form.line2} onChange={e => setForm(f => ({ ...f, line2: e.target.value }))} data-testid="checkout-address2" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>City *</Label>
                <Input required value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} data-testid="checkout-city" />
              </div>
              <div>
                <Label>State *</Label>
                <Input required value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} data-testid="checkout-state" />
              </div>
              <div>
                <Label>ZIP *</Label>
                <Input required value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} data-testid="checkout-zip" />
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-gray-50 p-4 space-y-1">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Order Summary</p>
              {cart.items.map(item => (
                <div key={`${item.product_id}_${item.variant_label}`} className="flex justify-between text-sm">
                  <span className="truncate">{item.product_name}{item.variant_label ? ` (${item.variant_label})` : ''} x{item.quantity}</span>
                  <span className="font-medium">${(item.unit_price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600"><span>Discount ({promoApplied?.code})</span><span>-${discountAmount.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-sm text-gray-500 pt-1"><span>Shipping</span><span>{actualShipping === 0 ? 'FREE' : `$${actualShipping.toFixed(2)}`}</span></div>
              <div className="flex justify-between font-bold text-lg border-t pt-1 mt-1"><span>Total</span><span>${total.toFixed(2)}</span></div>
            </div>

            {error && <p className="text-red-600 text-sm" data-testid="checkout-error">{error}</p>}

            <Button type="submit" disabled={submitting} className="w-full h-12 text-base" data-testid="pay-now-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</> : `Pay $${total.toFixed(2)}`}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};


// ===== WRAPPER =====
const ShopPage = () => (
  <CartProvider>
    <ShopPageContent />
  </CartProvider>
);

export default ShopPage;
